<script setup lang="ts">
import { router, useForm } from '@inertiajs/vue3'
import { CheckCircle, FileText, Pencil, RefreshCw, Send, Trash2 } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import AppDialog from '@/components/AppDialog.vue'
import InputError from '@/components/InputError.vue'
import QuoteLenderMenu from '@/components/applications/QuoteLenderMenu.vue'
import QuoteStatusBadge from '@/components/applications/QuoteStatusBadge.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useDialog } from '@/composables/useDialog'
import { formatAmount, formatDateTime } from '@/lib/utils'
import {
  acceptLoanApplicationQuotePath,
  loanApplicationQuotePath,
  loanApplicationQuotesPath,
  retryQuoteLoanApplicationQuotePath,
  sendCpaLoanApplicationQuotePath,
  submitLoanApplicationQuotePath,
} from '@/routes'

interface Lender {
  id: number
  name: string
  adapter: string | null
  adapterSupport: { create: boolean; submit_application: boolean; async: boolean }
}

interface Quote {
  id: number
  status: string
  lender_ref: string | null
  lender_status: string | null
  amount: number
  interest_rate: string
  term_months: number
  repayment: string
  lender_fees: number
  broker_fees: number | null
  notes: string | null
  cpa_sent_at: string | null
  submitted_at: string | null
  accepted_at: string | null
  created_at: string
  lender: Lender
}

const props = defineProps<{
  applicationId: string
  amount: number
  termMonths: number | null
  quotes: Quote[]
  lenders: Lender[]
  loanTerms: number[]
  readonly?: boolean
}>()

// ── Create / Edit dialog ───────────────────────────────────────────────────
const showDialog = ref(false)
const editingQuote = ref<Quote | null>(null)

const form = useForm({
  lender_id: '' as string | number,
  amount: props.amount as string | number,
  interest_rate: '' as string | number,
  term_months: props.termMonths ? String(props.termMonths) : ('' as string),
  repayment: '' as string | number,
  lender_fees: '' as string | number,
  broker_fees: 1690 as string | number,
  lender_ref: '',
  lender_status: '',
  notes: '',
})

const quotedLenderIds = computed(() => new Set(props.quotes.map((q) => q.lender.id)))
const availableLenders = computed(() => props.lenders.filter((l) => !quotedLenderIds.value.has(l.id)))

const createLender = ref<Lender | null>(null)
const creatingLenderId = ref<number | null>(null)

function pickLender(lender: Lender) {
  if (lender.adapterSupport.create) {
    createViaApi(lender)
  } else {
    openCreateForLender(lender)
  }
}

async function createViaApi(lender: Lender) {
  const ok = await confirm({
    title: `Request quote from ${lender.name}?`,
    description: 'This submits a live request to the lender.',
    confirmLabel: 'Request Quote',
    variant: 'default',
  })
  if (!ok) return
  creatingLenderId.value = lender.id
  router.post(
    loanApplicationQuotesPath(props.applicationId),
    { lender_id: lender.id },
    { preserveScroll: true, onFinish: () => { creatingLenderId.value = null } },
  )
}

function openCreateForLender(lender: Lender) {
  editingQuote.value = null
  form.reset()
  createLender.value = lender
  form.lender_id = lender.id
  showDialog.value = true
}

function openEdit(quote: Quote) {
  createLender.value = null
  editingQuote.value = quote
  form.lender_id = quote.lender.id
  form.amount = quote.amount
  form.interest_rate = quote.interest_rate
  form.term_months = String(quote.term_months)
  form.repayment = quote.repayment
  form.lender_fees = quote.lender_fees
  form.broker_fees = quote.broker_fees ?? ''
  form.lender_ref = quote.lender_ref ?? ''
  form.lender_status = quote.lender_status ?? ''
  form.notes = quote.notes ?? ''
  showDialog.value = true
}

function submitForm() {
  if (editingQuote.value) {
    form.patch(loanApplicationQuotePath(props.applicationId, editingQuote.value.id), {
      preserveScroll: true,
      onSuccess: () => {
        showDialog.value = false
      },
    })
  } else {
    form.post(loanApplicationQuotesPath(props.applicationId), {
      preserveScroll: true,
      onSuccess: () => {
        showDialog.value = false
      },
    })
  }
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}

// ── Actions ────────────────────────────────────────────────────────────────
const showSubmitConfirm = ref(false)
const actionQuote = ref<Quote | null>(null)
const sendPayslip = ref(false)

const isActionQuoteMoneyMe = computed(() => actionQuote.value?.lender.adapter === 'money_me')

const { confirm } = useDialog()

async function confirmSendCpa(quote: Quote) {
  const ok = await confirm({
    title: 'Send CPA?',
    description: `Send the Credit Proposal Agreement for the ${quote.lender.name} quote to the customer?`,
    confirmLabel: 'Send CPA',
    variant: 'default',
  })
  if (!ok) return
  router.patch(sendCpaLoanApplicationQuotePath(props.applicationId, quote.id), {}, { preserveScroll: true })
}

function confirmSubmit(quote: Quote) {
  actionQuote.value = quote
  sendPayslip.value = false
  showSubmitConfirm.value = true
}

function doSubmit() {
  if (!actionQuote.value) return
  router.patch(
    submitLoanApplicationQuotePath(props.applicationId, actionQuote.value.id),
    {
      ...(isActionQuoteMoneyMe.value && { send_payslip: sendPayslip.value }),
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        showSubmitConfirm.value = false
      },
    },
  )
}

async function confirmAccept(quote: Quote) {
  const ok = await confirm({
    title: 'Accept quote?',
    description: `Accept the quote from ${quote.lender.name}? This will mark the application as Approved and assign the lender.`,
    confirmLabel: 'Accept Quote',
    variant: 'default',
  })
  if (!ok) return
  router.patch(acceptLoanApplicationQuotePath(props.applicationId, quote.id), {}, { preserveScroll: true })
}

async function confirmDelete(quote: Quote) {
  const ok = await confirm({
    title: 'Remove quote?',
    description: `Remove the quote from ${quote.lender.name}? This cannot be undone.`,
    confirmLabel: 'Remove',
  })
  if (!ok) return
  router.delete(loanApplicationQuotePath(props.applicationId, quote.id), { preserveScroll: true })
}

// ── Can show actions per status ────────────────────────────────────────────
function canSendCpa(q: Quote) {
  return ['quoted', 'referred'].includes(q.status) && !props.readonly
}

function canSubmitToLender(q: Quote) {
  return q.status === 'cpa_sent' && q.lender.adapterSupport.submit_application && !props.readonly
}

function canAccept(q: Quote) {
  const afterSubmit = q.status === 'submitted'
  const directAccept = q.status === 'cpa_sent' && !q.lender.adapterSupport.submit_application
  return (afterSubmit || directAccept) && !props.readonly
}

function canEdit(q: Quote) {
  return !['submitted', 'accepted'].includes(q.status) && !props.readonly
}

function canDelete(q: Quote) {
  return q.status !== 'accepted' && !props.readonly
}

function canRetry(q: Quote) {
  return (
    q.status === 'pending' &&
    q.lender_ref &&
    q.lender.adapterSupport.async &&
    q.lender_status === 'FailedToRetrieve' &&
    !props.readonly
  )
}

function doRetry(quote: Quote) {
  router.patch(
    retryQuoteLoanApplicationQuotePath(props.applicationId, quote.id),
    {},
    { preserveScroll: true },
  )
}
</script>

<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-sm font-medium">Lender Quotes</h3>
        <p class="text-muted-foreground text-xs">{{ quotes.length }} quote{{ quotes.length !== 1 ? 's' : '' }}</p>
      </div>
      <QuoteLenderMenu
        v-if="!readonly && availableLenders.length > 0"
        :lenders="availableLenders"
        :pending="creatingLenderId !== null"
        label="Request Quote"
        align="end"
        @select="pickLender"
      />
    </div>

    <!-- Empty state -->
    <div
      v-if="quotes.length === 0"
      class="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed py-14 text-center"
    >
      <FileText class="text-muted-foreground/40 size-10" />
      <p class="text-muted-foreground text-sm">No quotes yet</p>
      <QuoteLenderMenu
        v-if="!readonly && availableLenders.length > 0"
        :lenders="availableLenders"
        :pending="creatingLenderId !== null"
        label="Request first quote"
        variant="outline"
        align="center"
        @select="pickLender"
      />
    </div>

    <!-- Quote cards -->
    <div v-else class="space-y-3">
      <div
        v-for="quote in quotes"
        :key="quote.id"
        class="border-border rounded-lg border p-4"
        :class="{ 'border-green-500 bg-green-50/30 dark:bg-green-950/20': quote.status === 'accepted' }"
      >
        <!-- Card header -->
        <div class="mb-3 flex items-start justify-between gap-3">
          <div class="space-y-0.5">
            <div class="flex items-center gap-2">
              <span class="font-medium">{{ quote.lender.name }}</span>
              <QuoteStatusBadge :status="quote.status" />
              <Badge v-if="quote.lender.adapterSupport.create || quote.lender.adapterSupport.submit_application" variant="outline" class="text-xs">API</Badge>
            </div>
            <p v-if="quote.lender_ref" class="text-muted-foreground text-xs">
              Ref: {{ quote.lender_ref }}
              <span v-if="quote.lender_status"> · {{ quote.lender_status }}</span>
            </p>
          </div>

          <!-- Actions -->
          <div class="flex shrink-0 items-center gap-1">
            <Button
              v-if="canSendCpa(quote)"
              variant="outline"
              size="sm"
              class="gap-1.5 text-xs"
              @click="confirmSendCpa(quote)"
            >
              <Send class="size-3" />
              Send CPA
            </Button>
            <Button
              v-if="canSubmitToLender(quote)"
              variant="outline"
              size="sm"
              class="gap-1.5 text-xs"
              @click="confirmSubmit(quote)"
            >
              <Send class="size-3" />
              Submit to Lender
            </Button>
            <Button
              v-if="canAccept(quote)"
              variant="outline"
              size="sm"
              class="gap-1.5 border-green-500 text-xs text-green-700 hover:bg-green-50 dark:text-green-400"
              @click="confirmAccept(quote)"
            >
              <CheckCircle class="size-3" />
              Accept
            </Button>
            <Button
              v-if="canRetry(quote)"
              variant="outline"
              size="sm"
              class="gap-1.5 text-xs"
              @click="doRetry(quote)"
            >
              <RefreshCw class="size-3" />
              Retry
            </Button>
            <Button v-if="canEdit(quote)" variant="ghost" size="icon" class="size-7" @click="openEdit(quote)">
              <Pencil class="size-3.5" />
            </Button>
            <Button
              v-if="canDelete(quote)"
              variant="ghost"
              size="icon"
              class="text-destructive hover:text-destructive size-7"
              @click="confirmDelete(quote)"
            >
              <Trash2 class="size-3.5" />
            </Button>
          </div>
        </div>

        <!-- Financial details (hidden for pending quotes) -->
        <div v-if="quote.status !== 'pending'" class="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          <div>
            <span class="text-muted-foreground text-xs">Amount</span>
            <p class="font-medium">{{ formatAmount(quote.amount) }}</p>
          </div>
          <div>
            <span class="text-muted-foreground text-xs">Interest Rate</span>
            <p class="font-medium">{{ quote.interest_rate }}%</p>
          </div>
          <div class="sm:col-start-3 sm:row-start-1">
            <span class="text-muted-foreground text-xs">Term</span>
            <p class="font-medium">{{ quote.term_months }} months</p>
          </div>
          <div class="sm:col-start-4 sm:row-start-1 sm:row-span-2">
            <span class="text-muted-foreground text-xs">Quoted</span>
            <p>{{ formatDateTime(quote.created_at) }}</p>
            <template v-if="quote.cpa_sent_at">
              <span class="text-muted-foreground mt-1 block text-xs">CPA Sent</span>
              <p>{{ formatDateTime(quote.cpa_sent_at) }}</p>
            </template>
            <template v-if="quote.submitted_at">
              <span class="text-muted-foreground mt-1 block text-xs">Submitted to Lender</span>
              <p class="text-blue-700 dark:text-blue-400">{{ formatDateTime(quote.submitted_at) }}</p>
            </template>
            <template v-if="quote.accepted_at">
              <span class="text-muted-foreground mt-1 block text-xs">Accepted</span>
              <p class="font-medium text-green-700 dark:text-green-400">{{ formatDateTime(quote.accepted_at) }}</p>
            </template>
          </div>
          <div class="sm:col-start-1 sm:row-start-2">
            <span class="text-muted-foreground text-xs">Lender Fees</span>
            <p>{{ formatAmount(quote.lender_fees) }}</p>
          </div>
          <div class="sm:col-start-2 sm:row-start-2">
            <span class="text-muted-foreground text-xs">Broker Fees</span>
            <p>{{ quote.broker_fees != null ? formatAmount(quote.broker_fees) : '—' }}</p>
          </div>
          <div class="sm:col-start-3 sm:row-start-2">
            <span class="text-muted-foreground text-xs">Monthly Repayment</span>
            <p class="font-medium">{{ formatAmount(Number(quote.repayment)) }}</p>
          </div>
        </div>

        <p v-if="quote.status === 'pending' && quote.lender_status === 'Rejected'" class="text-destructive text-sm">
          Quote request was rejected by lender.
        </p>
        <p v-else-if="quote.status === 'pending' && quote.lender_status === 'FailedToRetrieve'" class="text-destructive text-sm">
          Failed to fetch quote from lender. You can retry or delete this quote.
        </p>
        <p v-else-if="quote.status === 'pending'" class="text-muted-foreground text-sm">
          Awaiting quote from lender via API…
        </p>

        <p v-if="quote.notes" class="text-muted-foreground border-border mt-2 border-t pt-2 text-xs">
          {{ quote.notes }}
        </p>
      </div>
    </div>
  </div>

  <!-- ── Create / Edit Dialog ──────────────────────────────────────────────── -->
  <AppDialog
    v-model:open="showDialog"
    :title="editingQuote ? 'Edit Quote' : 'Request Quote'"
    :description="editingQuote ? 'Update the quote details.' : 'Add a quote from a lender.'"
    :loading="form.processing"
    loading-message="Saving…"
    scrollable
    class="sm:max-w-lg"
  >
    <div class="space-y-4">
      <!-- Lender (fixed — chosen before the dialog opened, or fixed on edit) -->
      <div class="grid gap-1">
        <Label class="text-muted-foreground text-xs">Lender</Label>
        <p class="text-sm font-medium">{{ editingQuote ? editingQuote.lender.name : createLender?.name }}</p>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="grid gap-2">
          <Label for="q-amount">Amount <span class="text-destructive">*</span></Label>
          <Input id="q-amount" v-model="form.amount" type="number" min="0" placeholder="25000" />
          <InputError :messages="err('amount')" />
        </div>
        <div class="grid gap-2">
          <Label for="q-interest">Interest Rate % <span class="text-destructive">*</span></Label>
          <Input
            id="q-interest"
            v-model="form.interest_rate"
            type="number"
            step="0.01"
            min="0"
            max="99"
            placeholder="9.99"
          />
          <InputError :messages="err('interest_rate')" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="grid gap-2">
          <Label for="q-term">Term <span class="text-destructive">*</span></Label>
          <Select v-model="form.term_months">
            <SelectTrigger id="q-term" class="w-full">
              <SelectValue placeholder="Select term" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="t in props.loanTerms" :key="t" :value="String(t)">
                {{ t }} months
              </SelectItem>
            </SelectContent>
          </Select>
          <InputError :messages="err('term_months')" />
        </div>
        <div class="grid gap-2">
          <Label for="q-repayment">Monthly Repayment <span class="text-destructive">*</span></Label>
          <Input id="q-repayment" v-model="form.repayment" type="number" step="0.01" min="0" placeholder="650.00" />
          <InputError :messages="err('repayment')" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="grid gap-2">
          <Label for="q-lender-fees">Lender Fees <span class="text-destructive">*</span></Label>
          <Input id="q-lender-fees" v-model="form.lender_fees" type="number" min="0" placeholder="0" />
          <InputError :messages="err('lender_fees')" />
        </div>
        <div class="grid gap-2">
          <Label for="q-broker-fees"
            >Broker Fees <span class="text-muted-foreground font-normal">(optional)</span></Label
          >
          <Input id="q-broker-fees" v-model="form.broker_fees" type="number" min="0" placeholder="0" />
          <InputError :messages="err('broker_fees')" />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="grid gap-2">
          <Label for="q-lender-ref"
            >Lender Ref <span class="text-muted-foreground font-normal">(optional)</span></Label
          >
          <Input id="q-lender-ref" v-model="form.lender_ref" placeholder="ABC123" />
        </div>
        <div class="grid gap-2">
          <Label for="q-lender-status"
            >Lender Status <span class="text-muted-foreground font-normal">(optional)</span></Label
          >
          <Input id="q-lender-status" v-model="form.lender_status" placeholder="Accepted" />
        </div>
      </div>

      <!-- Notes (always) -->
      <div class="grid gap-2">
        <Label for="q-notes">Notes <span class="text-muted-foreground font-normal">(optional)</span></Label>
        <Textarea id="q-notes" v-model="form.notes" :rows="2" placeholder="Additional information from lender…" />
        <InputError :messages="err('notes')" />
      </div>
    </div>
    <template #footer>
      <Button variant="outline" @click="showDialog = false">Cancel</Button>
      <Button :disabled="form.processing" @click="submitForm">
        {{ editingQuote ? 'Save Changes' : 'Add Quote' }}
      </Button>
    </template>
  </AppDialog>

  <!-- ── Submit to Lender Confirmation ────────────────────────────────────── -->
  <AppDialog
    v-model:open="showSubmitConfirm"
    title="Submit to Lender"
    :description="`Submit the full application to ${actionQuote?.lender.name} via their API? The lender will review it and confirm back to you separately.`"
  >
    <!-- MoneyMe document choice -->
    <div v-if="isActionQuoteMoneyMe" class="flex items-center gap-2">
      <Checkbox id="send-payslip" v-model:checked="sendPayslip" />
      <Label for="send-payslip" class="text-sm font-normal">Send payslip instead of bank statement</Label>
    </div>
    <template #footer>
      <Button variant="outline" @click="showSubmitConfirm = false">Cancel</Button>
      <Button @click="doSubmit">Submit to Lender</Button>
    </template>
  </AppDialog>
</template>
